import dayjs from 'dayjs'
import i18n from 'i18next'
import AmicaleApi from '../../../api/AmicaleApi'
import { CARD_TYPE_LABELS } from '../../admin/modules/GestionCartesAccesEtBienEtre/shared/constants'
import { formatPeriode } from '../../admin/modules/GestionCartesAccesEtBienEtre/shared/format'

const tq = (key, options) => i18n.t(`amicales.quote.${key}`, options)

const formatDate = (value) => {
  if (!value) return '-'
  const d = dayjs(value)
  return d.isValid() ? d.format('DD/MM/YYYY') : String(value)
}

const escapeHtml = (str) => {
  if (str == null) return ''
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export const buildAmicalCaQuoteHtml = ({
  carte,
  client,
  amicalForm,
  clientName,
  voucherEmail,
  date,
  pax,
  totalPrice,
  currency = 'TND',
  reservationRef,
  bgDataUri = null,
}) => {
  const today = dayjs().format('DD/MM/YYYY HH:mm')
  const titre = reservationRef
    ? tq('titleWithRef', { ref: reservationRef })
    : tq('title')
  const typeLabel = CARD_TYPE_LABELS[carte?.type] || carte?.type || tq('title')

  const bgStyle = bgDataUri
    ? `background-image: url('${bgDataUri}'); background-size: cover; background-position: center; background-repeat: no-repeat;`
    : ''

  const lang = (i18n.language || 'fr').split('-')[0]

  return `<!DOCTYPE html>
<html lang="${escapeHtml(lang)}">
<head>
  <meta charset="UTF-8" />
  <title>${escapeHtml(titre)}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; color: #1f2937; padding: 24px; ${bgStyle} min-height: 100vh; }
    h1 { font-size: 22px; margin: 0 0 4px 0; color: #1e3a8a; }
    h2 { font-size: 14px; margin: 18px 0 8px; color: #1e3a8a; border-bottom: 1px solid rgba(229,231,235,0.7); padding-bottom: 4px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1e3a8a; padding-bottom: 12px; margin-bottom: 16px; }
    .meta { font-size: 12px; color: #6b7280; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; font-size: 13px; }
    .info-grid div span:first-child { color: #6b7280; display: inline-block; min-width: 130px; }
    .info-grid div span:last-child { color: #111827; font-weight: 500; }
    .content { background: rgba(255,255,255,0.82); border-radius: 8px; padding: 20px; backdrop-filter: blur(2px); }
    .total { margin-top: 16px; text-align: right; font-size: 15px; }
    .total strong { color: #1e3a8a; font-size: 18px; }
  </style>
</head>
<body>
<div class="content">
  <div class="header">
    <div>
      <h1>${escapeHtml(titre)}</h1>
      <div class="meta">${escapeHtml(tq('editedOn', { date: today }))}</div>
    </div>
    <div style="text-align:right;">
      <div style="font-weight:600; color:#1e3a8a;">${escapeHtml(client?.name || tq('defaultClientName'))}</div>
      ${voucherEmail ? `<div style="font-size:12px; color:#6b7280;">${escapeHtml(voucherEmail)}</div>` : ''}
    </div>
  </div>

  <h2>${escapeHtml(typeLabel)}</h2>
  <div class="info-grid">
    <div><span>${escapeHtml(tq('product'))}</span> <span>${escapeHtml(carte?.label || '-')}</span></div>
    <div><span>${escapeHtml(tq('facility'))}</span> <span>${escapeHtml(carte?.facility_label || '-')}</span></div>
    <div><span>${escapeHtml(tq('period'))}</span> <span>${escapeHtml(formatPeriode(carte?.start_date, carte?.end_date) || '-')}</span></div>
    <div><span>${escapeHtml(tq('accessDate'))}</span> <span>${escapeHtml(formatDate(date))}</span></div>
    <div><span>${escapeHtml(tq('persons'))}</span> <span>${escapeHtml(pax ?? 1)}</span></div>
  </div>

  <h2>${escapeHtml(tq('clientSection'))}</h2>
  <div class="info-grid">
    <div><span>${escapeHtml(tq('name'))}</span> <span>${escapeHtml(clientName || '-')}</span></div>
    <div><span>${escapeHtml(tq('civility'))}</span> <span>${escapeHtml(amicalForm?.civilite_amical || '-')}</span></div>
    <div><span>${escapeHtml(tq('matricule'))}</span> <span>${escapeHtml(amicalForm?.matricule_fiscal_amical || '-')}</span></div>
    <div><span>${escapeHtml(tq('phone'))}</span> <span>${escapeHtml(amicalForm?.telephone_amical || '-')}</span></div>
  </div>

  <div class="total">
    ${escapeHtml(tq('estimatedTotal'))} <strong>${Number(totalPrice || 0).toFixed(2)} ${escapeHtml(currency)}</strong>
  </div>
</div>
</body>
</html>`
}

export const buildCaQuoteParamsFromReservation = (reservation, { amicalClient, voucherEmail } = {}) => ({
  carte: {
    label: reservation.carte_label,
    type: reservation.carte_type,
    facility_label: reservation.facility_label,
    start_date: reservation.carte_start_date,
    end_date: reservation.carte_end_date,
  },
  client: { name: amicalClient?.name || i18n.t('amicales.quote.defaultClientName') },
  amicalForm: {
    civilite_amical: reservation.civilite_amical,
    matricule_fiscal_amical:
      reservation.matricule_amical || reservation.matricule_fiscal_amical,
    telephone_amical: reservation.telephone_amical,
  },
  clientName: reservation.client_name,
  voucherEmail: voucherEmail || amicalClient?.voucherEmail,
  date: reservation.reservation_date || reservation.check_in,
  pax: reservation.pax ?? 1,
  totalPrice: reservation.total_amount,
  currency: reservation.currency || 'TND',
  reservationRef: reservation.ref || reservation.id_booking || reservation.id,
})

export const printAmicalCaQuote = async (params) => {
  const bgDataUri = await AmicaleApi.getVoucherBgDataUri().catch(() => null)
  const html = buildAmicalCaQuoteHtml({ ...params, bgDataUri })
  const iframe = document.createElement('iframe')
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0'
  document.body.appendChild(iframe)
  iframe.onload = () => {
    try {
      iframe.contentWindow.focus()
      iframe.contentWindow.print()
    } catch (err) {
      console.error('[AmicalCaQuote] print failed:', err)
    }
    setTimeout(() => {
      if (iframe.parentNode) document.body.removeChild(iframe)
    }, 1500)
  }
  const doc = iframe.contentDocument || iframe.contentWindow.document
  doc.open()
  doc.write(html)
  doc.close()
}

export const downloadAmicalCaQuote = async (params) => {
  const bgDataUri = await AmicaleApi.getVoucherBgDataUri().catch(() => null)
  const html = buildAmicalCaQuoteHtml({ ...params, bgDataUri })
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  const id = params?.reservationRef ? `-${params.reservationRef}` : ''
  const prefix = i18n.t('amicales.quote.downloadFilenamePrefix')
  a.href = url
  a.download = `${prefix}${id}.html`
  document.body.appendChild(a)
  a.click()
  setTimeout(() => {
    URL.revokeObjectURL(url)
    if (a.parentNode) document.body.removeChild(a)
  }, 100)
}
